import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import useCVDetail from '../hooks/useCVDetail';
import { CVHttpService } from '../services';
import { Utils } from 'core/utils';
import {
    Box,
    Button,
    Container,
    Typography,
    Stack,
    CircularProgress,
    Alert,
    Paper,
    IconButton,
    Chip,
    Divider,
    Breadcrumbs,
    Link as MuiLink,
    Menu,
    MenuItem,
    ListItemText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Link } from 'react-router-dom';
import WidgetCVTitle from '../components/WidgetCvTitle';
import SkillsSection from 'features/feature_user/components/profile/SkillsSection';
import EducationSection from 'features/feature_user/components/profile/EducationSection';
import LanguageSection from 'features/feature_user/components/profile/LanguageSection';
import ReferenceSection from 'features/feature_user/components/profile/ReferenceSection';
import CertificationSection from 'features/feature_user/components/profile/CertificationSection';
import JobExperienceSection from 'features/feature_user/components/profile/JobExperienceSection';
import MarkdownRenderer from '../components/MarkdownRenderer';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

function CVDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: cv, isLoading, error } = useCVDetail(id);
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [selectedStyle, setSelectedStyle] = useState('style2');
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

    const handleEdit = () => {
        // For AI-generated CVs, redirect to markdown editor
        if (isAIGenerated) {
            navigate(`/cv/ai-editor/${id}`);
        } else {
            // For regular CVs, use the structured builder
            navigate(`/cv/builder/${id}`);
        }
    };

    const handleDownloadPDF = async (style = selectedStyle) => {
        if (!cv?.id) return;
        setDownloading(true);
        try {
            // For AI-generated CVs, don't pass style parameter - backend will use AI template
            if (isAIGenerated) {
                await CVHttpService.downloadPDF(cv.id, null);
            } else {
                setSelectedStyle(style);
                await CVHttpService.downloadPDF(cv.id, style);
            }
            setDownloadMenuAnchor(null);
        } catch (err) {
            alert('Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadMenuOpen = (event) => {
        setDownloadMenuAnchor(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
        setDownloadMenuAnchor(null);
    };

    const cvStyles = [
        { value: 'style1', label: 'Style 1' },
        { value: 'style2', label: 'Style 2 (Default)' },
        { value: 'style3', label: 'Style 3 (Minimalist)' },
    ];

    const handleShare = async () => {
        if (!cv?.share_uuid) {
            alert('Shareable link not available for this CV');
            return;
        }
        const shareUrl = CVHttpService.getShareableURL(cv.share_uuid);
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Failed to copy link');
        }
    };

    const handleBackToList = () => {
        navigate('/cv');
    };

    const handlePrint = () => {
        if (cv?.share_uuid) {
            window.open(`/cv/print/${cv.share_uuid}`, '_blank');
        } else if (cv?.id) {
            window.open(`/cv/print/id/${cv.id}`, '_blank');
        }
    };

    const pageShell = (children) => (
        <>
            <Header />
            <Box sx={{ minHeight: 'calc(100vh - 56px)', bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', pt: { xs: '60px', md: '68px' } }}>
                <Container maxWidth="lg" sx={{ pt: 3, pb: 4, flex: 1 }}>
                    {children}
                </Container>
            </Box>
            <MinimalFooter />
        </>
    );

    if (isLoading) {
        return pageShell(
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !cv) {
        return pageShell(
            <>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error ? 'Failed to load CV' : 'CV not found'}
                </Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBackToList} sx={{ textTransform: 'none' }}>
                    Back to My CVs
                </Button>
            </>
        );
    }

    const shareUrl = cv.share_uuid ? CVHttpService.getShareableURL(cv.share_uuid) : null;

    // Check if this is an AI-generated CV (markdown format in description)
    const isAIGenerated = cv.description && (
        cv.description.trim().startsWith('#') || 
        cv.description.includes('##') ||
        cv.description.includes('**') ||
        (cv.description.includes('---') && cv.description.split('\n').length > 10)
    );

    return pageShell(
        <Stack spacing={2.5}>
                    {/* Breadcrumbs */}
                    <Breadcrumbs aria-label="breadcrumb">
                        <MuiLink
                            component={Link}
                            to="/cv"
                            color="inherit"
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            My CVs
                        </MuiLink>
                        <Typography color="text.primary">{formatText(cv.title)}</Typography>
                    </Breadcrumbs>

                    {/* Header Actions */}
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            <IconButton onClick={handleBackToList} size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" fontWeight={700}>
                                    {formatText(cv.title)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || 'No name')}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={handleEdit}
                                    size="small"
                                >
                                    Edit CV
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={handlePrint}
                                    size="small"
                                >
                                    Print View
                                </Button>
                                {shareUrl && (
                                    <Button
                                        variant="outlined"
                                        startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                        onClick={handleShare}
                                        color={copied ? 'success' : 'primary'}
                                        size="small"
                                    >
                                        {copied ? 'Copied!' : 'Copy Share Link'}
                                    </Button>
                                )}
                                {cv.share_uuid && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<ShareIcon />}
                                        onClick={() => window.open(`/cv/share/${cv.share_uuid}`, '_blank')}
                                        size="small"
                                    >
                                        View Public
                                    </Button>
                                )}
                                {isAIGenerated ? (
                                    // For AI-generated CVs, show simple download button without style selection
                                    <Button
                                        variant="contained"
                                        startIcon={<PictureAsPdfIcon />}
                                        onClick={() => handleDownloadPDF()}
                                        disabled={downloading}
                                        size="small"
                                    >
                                        {downloading ? 'Downloading...' : 'Download PDF'}
                                    </Button>
                                ) : (
                                    // For regular CVs, show style selection menu
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<PictureAsPdfIcon />}
                                            endIcon={<ArrowDropDownIcon />}
                                            onClick={handleDownloadMenuOpen}
                                            disabled={downloading}
                                            size="small"
                                        >
                                            {downloading ? 'Downloading...' : 'Download PDF'}
                                        </Button>
                                        <Menu
                                            anchorEl={downloadMenuAnchor}
                                            open={Boolean(downloadMenuAnchor)}
                                            onClose={handleDownloadMenuClose}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right',
                                            }}
                                        >
                                            {cvStyles.map((style) => (
                                                <MenuItem
                                                    key={style.value}
                                                    onClick={() => handleDownloadPDF(style.value)}
                                                    selected={selectedStyle === style.value}
                                                >
                                                    <ListItemText primary={style.label} />
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>

                        {shareUrl && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                                        Share link:
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {shareUrl}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Paper>

                    {/* CV Content */}
                    {isAIGenerated ? (
                        /* AI-Generated CV (Markdown Format) */
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <Chip label="AI Generated" color="primary" size="small" />
                                <Typography variant="subtitle1" fontWeight={600}>CV Preview</Typography>
                            </Stack>
                            <MarkdownRenderer markdown={cv.description} />
                        </Paper>
                    ) : (
                        /* Regular CV Format */
                        <>
                            <WidgetCVTitle cv={cv} editable={false} />

                            {/* About/Description */}
                            {cv.description && (
                                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        About
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                        {formatText(cv.description)}
                                    </Typography>
                                </Paper>
                            )}

                            {/* CV Sections — read-only */}
                            <JobExperienceSection cv={cv} readonly={true} />
                            <SkillsSection cv={cv} readonly={true} />
                            <EducationSection cv={cv} readonly={true} />
                            <LanguageSection cv={cv} readonly={true} />
                            <ReferenceSection cv={cv} readonly={true} />
                            <CertificationSection cv={cv} readonly={true} />
                        </>
                    )}

                    {/* Interests — always shown regardless of CV format */}
                    {cv.interests && (
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Interests
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                {formatText(cv.interests)}
                            </Typography>
                        </Paper>
                    )}

        </Stack>
    );
}

export default CVDetailsPage;
